/**
 * Production Monthly Output — legacy `modules/sftsRpt/output/productionOutput`.
 *
 * One server report in four governed views: `detail` (factory × date, MV + dynamic pivot),
 * `by-date` (one row per date, when no factory is chosen), `summary` (long form, operation × date,
 * for the matrix and cards) and `mo` (one row per manufacturing order).
 *
 * Detail and Summary are ungated; MO is access-gated and lazy, because it exposes unit price and FOB.
 */

import { useCallback, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { DataTable } from '@/components/DataTable';
import { FilterBar } from '@/components/FilterBar';
import { ReportPage } from '@/components/ReportPage';
import { ExportMenu } from '@/components/report/ExportMenu';
import { exportColumns, groupedColumns, integer } from '@/components/report/groupedColumns';
import { useReportFilters, useReportSort } from '@/components/report/useReportFilters';
import { Button } from '@/components/ui/button';
import { useReportQuery } from '@/hooks/useReportQuery';
import { useAuth } from '@/lib/auth';
import type { ReportQueryRequest, SortSpec } from '@/lib/api';
import { cn } from '@/lib/utils';

import { OutputCards } from './cards';
import { MoExceptionSummary, MoTable } from './mo';
import { moExportColumns } from './moColumns';
import { isMoExceptionKey, type MoExceptionKey } from './moState';
import { preferredFactory, productionOutputFilters } from './filters';
import { SummaryTable, type SummaryMetric, type SummaryRow } from './summary';

type Tab = 'detail' | 'summary' | 'mo';

const REPORT_ID = 'production-monthly-output';

const DEFAULT_DETAIL_SORT: SortSpec[] = [{ field: 'daily_date', dir: 'asc' }];

function isTab(value: string | null): value is Tab {
  return value === 'detail' || value === 'summary' || value === 'mo';
}

function isSummaryMetric(value: string | null): value is SummaryMetric {
  return (
    value === 'pcs'
    || value === 'smv'
    || value === 'revenue'
    || value === 'matcostval'
    || value === 'matcost'
  );
}

export default function ProductionMonthlyOutputReport() {
  const { user, status } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const { values, isComplete, setValue } = useReportFilters(productionOutputFilters);
  // Ascending: a monthly report reads forward through the month, as the legacy screen did (§4.6).
  const { sort, setSort } = useReportSort(DEFAULT_DETAIL_SORT);

  const canSeeMo = (user?.roles ?? []).some(
    (role) => role === 'Production Monthly - MO Price' || role === 'MIS Admin',
  );

  const requestedTab = searchParams.get('view');
  const tab: Tab = isTab(requestedTab) && (requestedTab !== 'mo' || canSeeMo)
    ? requestedTab
    : 'detail';
  const requestedMetric = searchParams.get('metric');
  const summaryMetric: SummaryMetric = isSummaryMetric(requestedMetric) ? requestedMetric : 'pcs';
  const requestedException = searchParams.get('moException');
  const moException: MoExceptionKey | null = isMoExceptionKey(requestedException)
    ? requestedException
    : null;

  const setUrlParam = useCallback(
    (name: string, value: string | null) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          if (value === null) next.delete(name);
          else next.set(name, value);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // Write the login site into the URL, so the default is shareable and not read as explicit "all".
  useEffect(() => {
    if (status !== 'authenticated' || user === null) return;
    if (searchParams.has('factory')) return;
    const preferred = preferredFactory(user.defaultFactory);
    setValue('factory', preferred);
  }, [status, user, searchParams, setValue]);

  const byDate = (values.factory ?? '') === '';
  const detailView = byDate ? 'by-date' : 'detail';

  const baseFilters = useMemo<ReportQueryRequest['filters']>(
    () => ({
      from: values.from ?? null,
      to: values.to ?? null,
      factory: values.factory ?? null,
      brand: values.brand ?? null,
    }),
    [values],
  );

  const detailFilters = useMemo<ReportQueryRequest['filters']>(
    () =>
      // `by-date` does not declare `factory`, so sending it is a 400 rather than an ignored key.
      byDate
        ? { from: values.from ?? null, to: values.to ?? null, brand: values.brand ?? null }
        : baseFilters,
    [byDate, values, baseFilters],
  );

  const detailSort = useMemo(
    () => (byDate ? sort.filter((spec) => spec.field !== 'factory') : sort),
    [byDate, sort],
  );

  const detail = useReportQuery<Record<string, unknown>>(REPORT_ID, detailFilters, {
    view: detailView,
    enabled: isComplete && tab === 'detail',
  });
  const summary = useReportQuery<SummaryRow>(REPORT_ID, baseFilters, {
    view: 'summary',
    enabled: isComplete && tab !== 'mo',
  });
  // MO is deliberately lazy: eligible readers pay for this large query only after opening it.
  const mo = useReportQuery<Record<string, unknown>>(REPORT_ID, baseFilters, {
    view: 'mo',
    enabled: isComplete && canSeeMo && tab === 'mo',
    staleTime: 10 * 60 * 1000,
  });

  const pivotPrefixes = [
    'cutting__',
    'bundling__',
    'sewing__',
    'qc__',
    'super_market__',
    'eins_revenue__',
  ];
  const detailRows = detail.data?.rows ?? [];
  const columns = groupedColumns<Record<string, unknown>>(detail.data?.columns ?? [], {
    hide: (c) => {
      if (c.group === 'Data quality') return true;
      if (!pivotPrefixes.some((p) => c.field.startsWith(p))) return false;
      if (detailRows.length === 0) return false;
      return detailRows.every((row) => {
        const v = row[c.field];
        return v === null || v === undefined || v === '';
      });
    },
  });

  const detailTotals =
    detail.data !== undefined && !detail.data.truncated
      ? {
          labelColumn: 'factory',
          label: 'Total',
          format: (value: number) => integer.format(Math.round(value)),
        }
      : undefined;

  const moSort = useMemo<SortSpec[]>(() => {
    const raw = searchParams.get('moSort');
    if (raw === null) return [{ field: 'factory', dir: 'asc' }];
    const separator = raw.lastIndexOf(':');
    const field = raw.slice(0, separator);
    const dir = raw.slice(separator + 1);
    return field !== '' && (dir === 'asc' || dir === 'desc')
      ? [{ field, dir }]
      : [{ field: 'factory', dir: 'asc' }];
  }, [searchParams]);

  const moExportFilters = useMemo(() => {
    const filters: ReportQueryRequest['filters'] = { ...baseFilters };
    const urlToServer = {
      factory: 'mo_factory',
      brand: 'mo_brand',
      mo: 'mo',
      po: 'po',
      working_no: 'working_no',
      article_no: 'article_no',
      season: 'season',
    } as const;
    for (const [urlField, serverField] of Object.entries(urlToServer)) {
      const selected = searchParams.getAll(`moFilter.${urlField}`);
      if (selected.length > 0) filters[serverField] = selected;
    }
    if (moException !== null) filters.exception = moException;
    return filters;
  }, [baseFilters, searchParams, moException]);

  // Detail and MO draw a subset of their columns, so each tells the export what it drew (§5.9).
  // Summary sends none — its columns are dates, not the view's; known gap, see design-notes.md.
  const exportTarget =
    tab === 'mo'
      ? {
          request: { view: 'mo', filters: moExportFilters, sort: moSort },
          columns: moExportColumns(mo.data?.columns ?? []),
        }
      : tab === 'summary'
        ? {
            request: { view: 'summary', filters: baseFilters },
            columns: undefined,
          }
        : {
            request: { view: detailView, filters: detailFilters, sort: detailSort },
            columns: exportColumns(columns),
          };

  const viewTabs = (
    <div role="tablist" aria-label="View" className="flex gap-0.5 border-b border-border">
      {(
        [
          ['detail', 'Detail'],
          ['summary', 'Summary'],
          ...(canSeeMo ? ([['mo', 'MO']] as const) : []),
        ] as const
      ).map(([id, label]) => (
        <Button
          key={id}
          role="tab"
          variant="ghost"
          aria-selected={tab === id}
          onClick={() => {
            setUrlParam('view', id);
          }}
          className={cn(
            'h-8 rounded-none border-b-2 border-transparent px-3 text-sm',
            tab === id && 'border-primary font-medium text-foreground',
          )}
        >
          {label}
        </Button>
      ))}
    </div>
  );

  return (
    <ReportPage
      title="Production Monthly Output"
      description="Cutting through shipment for the period, with revenue, minutes and material cost by stage."
      filters={
        <FilterBar
          schema={productionOutputFilters}
          actions={
            <ExportMenu
              reportId={REPORT_ID}
              request={exportTarget.request}
              columns={exportTarget.columns}
              // Export re-runs server-side at the higher cap, so truncation is a reason to export.
              disabled={!isComplete}
            />
          }
        />
      }
      // One card row at a time: MO shows exception cards instead of range totals, as legacy did.
      summary={
        tab === 'mo' ? (
          <MoExceptionSummary
            rows={mo.data?.rows ?? []}
            isLoading={mo.isLoading}
            truncated={mo.data?.truncated === true}
            selected={moException}
            onSelectedChange={(next) => {
              setUrlParam('moException', next);
            }}
          />
        ) : (
          <OutputCards
            rows={summary.data?.rows ?? []}
            isLoading={summary.isLoading}
            truncated={summary.data?.truncated === true}
          />
        )
      }
      views={viewTabs}
      isLoading={
        tab === 'detail'
          ? detail.isLoading || summary.isLoading
          : tab === 'summary'
            ? summary.isLoading
            : mo.isLoading
      }
      error={
        tab === 'detail'
          ? (detail.error ?? summary.error)
          : tab === 'summary'
            ? summary.error
            : mo.error
      }
      generatedAt={
        tab === 'detail'
          ? detail.data?.generatedAt
          : tab === 'summary'
            ? summary.data?.generatedAt
            : mo.data?.generatedAt
      }
      dataAsOf={
        tab === 'detail'
          ? detail.data?.dataAsOf
          : tab === 'summary'
            ? summary.data?.dataAsOf
            : mo.data?.dataAsOf
      }
      truncated={
        tab === 'detail'
          ? detail.data?.truncated === true || summary.data?.truncated === true
          : tab === 'summary'
            ? summary.data?.truncated === true
            : mo.data?.truncated === true
      }
      table={
        tab === 'mo' ? (
          <MoTable data={mo.data} isLoading={mo.isLoading} exceptionFilter={moException} />
        ) : tab === 'detail' ? (
          <DataTable
            columns={columns}
            data={detail.data?.rows ?? []}
            isLoading={detail.isLoading}
            sort={sort}
            onSortChange={setSort}
            totals={detailTotals}
            truncated={detail.data?.truncated === true}
            caption="Production output by factory and date"
            emptyMessage="Nothing was recorded for these filters. Widen the dates, or clear the factory and brand."
          />
        ) : (
          <SummaryTable
            rows={summary.data?.rows ?? []}
            isLoading={summary.isLoading}
            metric={summaryMetric}
            truncated={summary.data?.truncated === true}
            onMetricChange={(next) => {
              setUrlParam('metric', next);
            }}
          />
        )
      }
    />
  );
}
